import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Box,
  Alert,
  Typography,
  Container,
  Modal,
  Snackbar,
  Paper,
  Tabs,
  Tab,
  Fade,
  Backdrop,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import {
  LockOutlined as LockOutlinedIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { connect } from 'react-redux';
import { fetchToken, otpRequest, verifyTOTP } from '../../redux';
import { authErrors, isAuthenticated } from '../../redux/rootReducer';
import { useNavigate } from "react-router-dom";
import { getAuthState, getTokenState } from '../../redux/auth/authSelectors';
import handleAxiosError from '../../redux/utils/handleAxiosError';
import setAuthToken from '../../redux/auth/setAuthToken';
import { showAlert, alertReset } from '../../redux';
import { MuiOtpInput } from 'mui-one-time-password-input';

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://www.codingnap.com/">
        Codingnap
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const theme = createTheme();

function SignIn(props) {
  const { isAuthenticated } = props;
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState('');
  const [isTOTPSet, setIsTOTPSet] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('otp'); // 'otp' or 'totp'
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const navigate = useNavigate();

  const handleOTPModelOpen = () => setOpen(true);
  const handleOTPModelClose = () => {
    setOpen(false);
    setOtp('');
    setVerificationMethod('otp');
    setOtpSent(false);
  };

  const handleOTPChange = (newValue) => {
    setOtp(newValue);
  };

  useEffect(() => {
    setError(handleAxiosError(props?.authState?.error));
  }, [props.authState]);

  useEffect(() => {
    if (isAuthenticated) {
      return navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleVerifyTOTP = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const response = await props.verifyTOTP({ email, password, otp }).unwrap();
      localStorage.setItem('JWT_access_token', response.data.access);
      localStorage.setItem('JWT_refresh_token', response.data.refresh);
      setAuthToken(response.data.access);
      props.showAlert("TOTP verified successfully!", "success");
      navigate('/dashboard');
    } catch (rejectedValueOrSerializedError) {
      props.showAlert("TOTP verification failed!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const response = await props.fetchToken({ email, otp }).unwrap();
      localStorage.setItem('JWT_access_token', response.access);
      localStorage.setItem('JWT_refresh_token', response.refresh);
      setAuthToken(response.access);
      props.showAlert("OTP verified successfully!", "success");
      navigate('/dashboard');
    } catch (rejectedValueOrSerializedError) {
      props.showAlert("OTP verification failed!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetOTP = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      await props.otpRequest({ email, password, get_otp: true }).unwrap();
      setOtpSent(true);
      setVerificationMethod('otp');
      props.showAlert("OTP sent to your email!", "success");
    } catch (rejectedValueOrSerializedError) {
      props.showAlert("Failed to send OTP!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      let data = await props.otpRequest({ email, password }).unwrap();
      if (data.data?.is_totp_set) {
        setIsTOTPSet(true);
        setVerificationMethod('totp');
      } else {
        setVerificationMethod('otp');
      }
      handleOTPModelOpen();
    } catch (rejectedValueOrSerializedError) {
      props.showAlert("Authentication failed", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    props.alertReset();
  };

  const handleMethodChange = (event, newValue) => {
    setVerificationMethod(newValue);
    setOtp('');
  };

  return (
    <ThemeProvider theme={theme}>
      {props.alert.message.length > 0 && (
        <Snackbar
          open={props.alert.message.length > 0}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseAlert} severity={props.alert.type} sx={{ width: '100%' }}>
            {props.alert.message}
          </Alert>
        </Snackbar>
      )}

      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              onChange={(e) => setPassword(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link href="#" variant="body2">
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link href="#" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
            <Grid item>
              {error.length > 0 && <Alert severity="error" sx={{ mt: 2 }}>{error}!</Alert>}
            </Grid>
          </Box>
        </Box>
        <Copyright sx={{ mt: 8, mb: 4 }} />
      </Container>

      {/* Enhanced OTP/TOTP Modal */}
      <Modal
        open={open}
        onClose={handleOTPModelClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500 },
              maxWidth: 500,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 0,
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 3,
                position: 'relative'
              }}
            >
              <IconButton
                onClick={handleOTPModelClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white'
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <SecurityIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" component="h2" fontWeight="bold">
                    Verify Your Identity
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Enter the verification code to continue
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {/* Tabs for switching between OTP and TOTP */}
              {isTOTPSet && (
                <Box sx={{ mb: 3 }}>
                  <Tabs
                    value={verificationMethod}
                    onChange={handleMethodChange}
                    variant="fullWidth"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500
                      }
                    }}
                  >
                    <Tab
                      value="totp"
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneAndroidIcon fontSize="small" />
                          <span>Authenticator App</span>
                        </Box>
                      }
                    />
                    <Tab
                      value="otp"
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailIcon fontSize="small" />
                          <span>Email OTP</span>
                        </Box>
                      }
                    />
                  </Tabs>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {/* OTP/TOTP Input */}
              <Box component="form" onSubmit={verificationMethod === 'otp' ? handleOTPSubmit : handleVerifyTOTP}>
                <Box sx={{ mb: 3 }}>
                  {verificationMethod === 'totp' ? (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <PhoneAndroidIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="500">
                          Enter code from your authenticator app
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Open your authenticator app and enter the 6-digit code
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <EmailIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="500">
                          Enter code from your email
                        </Typography>
                      </Box>
                      {otpSent ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          OTP has been sent to {email}
                        </Alert>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Click "Send OTP" to receive a code via email
                        </Typography>
                      )}
                    </Box>
                  )}

                  <MuiOtpInput
                    length={6}
                    value={otp}
                    onChange={handleOTPChange}
                    TextFieldsProps={{
                      size: 'medium',
                      placeholder: '-'
                    }}
                    sx={{
                      gap: 1,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }
                    }}
                  />
                </Box>

                {/* Action Buttons */}
                <Box display="flex" flexDirection="column" gap={2}>
                  {verificationMethod === 'otp' && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleGetOTP}
                      disabled={isLoading || otpSent}
                      startIcon={<EmailIcon />}
                    >
                      {otpSent ? 'OTP Sent' : 'Send OTP to Email'}
                    </Button>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={otp.length !== 6 || isLoading || (verificationMethod === 'otp' && !otpSent)}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </Box>

                {/* Help Text */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {verificationMethod === 'totp'
                      ? "Don't have access to your authenticator app?"
                      : "Didn't receive the code?"}
                  </Typography>
                  {verificationMethod === 'totp' && isTOTPSet && (
                    <Button
                      size="small"
                      onClick={() => setVerificationMethod('otp')}
                      sx={{ display: 'block', mx: 'auto', mt: 1 }}
                    >
                      Use Email OTP Instead
                    </Button>
                  )}
                  {verificationMethod === 'otp' && otpSent && (
                    <Button
                      size="small"
                      onClick={handleGetOTP}
                      disabled={isLoading}
                      sx={{ display: 'block', mx: 'auto', mt: 1 }}
                    >
                      Resend Code
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </ThemeProvider>
  );
}

const mapStateToProps = state => {
  return {
    authState: getAuthState(state),
    tokenState: getTokenState(state),
    isAuthenticated: isAuthenticated(),
    authErrors: authErrors(state),
    alert: state.alert,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchToken: (value) => dispatch(fetchToken(value)),
    otpRequest: (data) => dispatch(otpRequest(data)),
    showAlert: (message, type) => dispatch(showAlert(message, type)),
    alertReset: () => dispatch(alertReset()),
    verifyTOTP: (value) => dispatch(verifyTOTP(value)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SignIn);

